// Service API pour la gestion des composants et collections

import { API_CONFIG } from '../config/api.config';
import {
  User,
  AuthResponse,
  Component,
  Collection,
  ComponentsResponse,
  CollectionsResponse,
  CreateComponentData,
  UpdateComponentData,
  CreateCollectionData,
  ComponentCategory,
  ComponentTag,
  CreateRatingData,
  ComponentFilters
} from '../types/ComponentTypes';
import { emitUnauthorized } from '../utils/authEvents';

// Classe pour gérer les erreurs API
export class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

// ✅ CORRECTION : Fonction simplifiée pour récupérer le token de manière fiable
// Elle vérifie 'accessToken' (nouveau) PUIS 'token' (legacy)
const migrateLegacyToken = () => {
  const legacy = localStorage.getItem('token');
  const current = localStorage.getItem('accessToken');
  if (legacy && !current) {
    localStorage.setItem('accessToken', legacy);
  }
  if (legacy) {
    localStorage.removeItem('token');
  }
};

async function getValidToken(): Promise<string | null> {
  migrateLegacyToken();

  const accessToken = localStorage.getItem('accessToken');

  const decodeJwtPayload = (token: string): any | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4 || 4)) % 4, '=');
      const json = atob(padded);
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  const isExpired = (t: string): boolean => {
    const payload = decodeJwtPayload(t);
    if (!payload || typeof payload.exp !== 'number') return false;
    return Date.now() >= payload.exp * 1000;
  };

  const pickValid = (t?: string | null) =>
    t && t !== 'undefined' && t !== 'null' && !isExpired(t) ? t : null;

  const validAccess = pickValid(accessToken);
  if (validAccess) return validAccess;

  const refreshToken = localStorage.getItem('refreshToken');
  if (refreshToken) {
    try {
      const response = await authAPI.refreshToken();
      return response.accessToken || null;
    } catch {
      localStorage.removeItem('accessToken');
      return null;
    }
  }

  return null;
}

// Fonction utilitaire pour faire des requêtes HTTP
export async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getValidToken();
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      // On n'ajoute Auth que si le token est valide et non vide
      ...(token && { Authorization: `Bearer ${token}` }), 
      ...options.headers,
    },
    ...options,
  };

  const url = API_CONFIG.getApiUrl(endpoint);
  
  const response = await fetch(url, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Erreur inconnue' }));
    
    // ✅ CORRECTION MAJEURE : On log le 401 mais on ne force plus le logout brutal ici
    if (response.status === 401) {
      console.warn(`⚠️ 401 Unauthorized sur ${endpoint} - Token: ${token ? 'Présent' : 'Absent'}`);
      emitUnauthorized({ source: 'fetch', endpoint });
    }
    
    throw new APIError(response.status, errorData.message || `Erreur ${response.status}`);
  }

  return response.json();
}

// Service d'authentification
export const authAPI = {
  async login(email: string, password: string): Promise<AuthResponse> {
    console.log('🔐 Tentative de connexion pour:', email);
    const response = await fetchAPI<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // ✅ Sauvegarder les tokens SOUS LES DEUX CLES pour compatibilité maximale
    if (response.accessToken) {
      localStorage.setItem('accessToken', response.accessToken);
    }
    if (response.refreshToken) {
      localStorage.setItem('refreshToken', response.refreshToken);
    }
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  },

  async register(email: string, password: string, firstName: string, lastName?: string): Promise<AuthResponse> {
    const response = await fetchAPI<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName }),
    });

    if (response.accessToken) {
      localStorage.setItem('accessToken', response.accessToken);
    }
    if (response.refreshToken) {
      localStorage.setItem('refreshToken', response.refreshToken);
    }
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible');
    }

    // Appel direct fetch pour éviter boucle infinie avec fetchAPI
    const url = API_CONFIG.getApiUrl('/auth/refresh');
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) throw new Error('Echec du refresh token');
    
    const data = await response.json();

    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }

    return data;
  },

  async getProfile(): Promise<{ success: boolean; user: User }> {
    const user = await fetchAPI<User>('/users/me');
    return { success: true, user };
  },

  async updateProfile(profileData: {
    name?: string;
    email: string;
    firstName?: string;
    lastName?: string;
  }): Promise<{ success: boolean; user: User }> {
    const user = await fetchAPI<User>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return { success: true, user };
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return fetchAPI<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    return fetchAPI<{ success: boolean; message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
    return fetchAPI<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  logout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },
};

// Service de gestion des composants
export const componentsAPI = {
  async getComponents(filters: ComponentFilters = {}, page: number = 1, limit: number = 20): Promise<ComponentsResponse> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.search && { search: filters.search }),
      ...(filters.category && { category: filters.category }),
      ...(filters.tags && filters.tags.length > 0 && { tags: filters.tags.join(',') }),
      ...(filters.sortBy && { sortBy: filters.sortBy }),
      ...(filters.order && { order: filters.order }),
      ...(filters.isFavorite !== undefined && { isFavorite: filters.isFavorite.toString() }),
    });
    return fetchAPI<ComponentsResponse>(`/components?${queryParams}`);
  },
  async getComponent(id: number): Promise<Component> { return fetchAPI<Component>(`/components/${id}`); },
  async createComponent(data: CreateComponentData): Promise<Component> { return fetchAPI<Component>('/components', { method: 'POST', body: JSON.stringify(data) }); },
  async updateComponent(id: number, data: UpdateComponentData): Promise<Component> { return fetchAPI<Component>(`/components/${id}`, { method: 'PUT', body: JSON.stringify(data) }); },
  async deleteComponent(id: number): Promise<{ message: string }> { return fetchAPI<{ message: string }>(`/components/${id}`, { method: 'DELETE' }); },
  async duplicateComponent(id: number, name?: string): Promise<Component> { return fetchAPI<Component>(`/components/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ name }) }); },
  async toggleFavorite(id: number): Promise<{ isFavorite: boolean; message: string }> { return fetchAPI<{ isFavorite: boolean; message: string }>(`/components/${id}/favorite`, { method: 'POST' }); },
  async rateComponent(id: number, data: CreateRatingData): Promise<any> { return fetchAPI(`/components/${id}/rate`, { method: 'POST', body: JSON.stringify(data) }); },
  async getCategories(): Promise<ComponentCategory[]> { return fetchAPI<ComponentCategory[]>('/components/categories'); },
  async getTags(): Promise<ComponentTag[]> { return fetchAPI<ComponentTag[]>('/components/tags'); },
};

// Service de gestion des collections
export const collectionsAPI = {
  async getCollections(search?: string, page: number = 1, limit: number = 20): Promise<CollectionsResponse> {
    const queryParams = new URLSearchParams({ page: page.toString(), limit: limit.toString(), ...(search && { search }) });
    return fetchAPI<CollectionsResponse>(`/collections?${queryParams}`);
  },
  async getCollection(id: number): Promise<Collection> { return fetchAPI<Collection>(`/collections/${id}`); },
  async createCollection(data: CreateCollectionData): Promise<Collection> { return fetchAPI<Collection>('/collections', { method: 'POST', body: JSON.stringify(data) }); },
  async updateCollection(id: number, data: Partial<CreateCollectionData>): Promise<Collection> { return fetchAPI<Collection>(`/collections/${id}`, { method: 'PUT', body: JSON.stringify(data) }); },
  async deleteCollection(id: number): Promise<{ message: string }> { return fetchAPI<{ message: string }>(`/collections/${id}`, { method: 'DELETE' }); },
  async duplicateCollection(id: number, name?: string): Promise<Collection> { return fetchAPI<Collection>(`/collections/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ name }) }); },
  async addComponentToCollection(collectionId: number, componentId: number): Promise<any> { return fetchAPI(`/collections/${collectionId}/components`, { method: 'POST', body: JSON.stringify({ componentId }) }); },
  async removeComponentFromCollection(collectionId: number, componentId: number): Promise<{ message: string }> { return fetchAPI<{ message: string }>(`/collections/${collectionId}/components/${componentId}`, { method: 'DELETE' }); },
};

export const api = {
  get: <T>(endpoint: string): Promise<T> => fetchAPI<T>(endpoint),
  post: <T>(endpoint: string, body: any): Promise<T> => fetchAPI<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(endpoint: string, body: any): Promise<T> => fetchAPI<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(endpoint: string): Promise<T> => fetchAPI<T>(endpoint, { method: 'DELETE' }),
};

export const apiUtils = {
  async withRetry<T>(apiCall: () => Promise<T>, maxRetries: number = 1): Promise<T> {
    let lastError: Error;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        lastError = error as Error;
        if (error instanceof APIError && error.status === 401 && attempt < maxRetries) {
          try {
            await authAPI.refreshToken();
            continue; 
          } catch (refreshError) {
             console.warn("Retry failed: Refresh token not possible", refreshError);
             // On ne relance pas l'erreur refresh, on laisse la boucle finir ou l'erreur initiale remonter
             // pour ne pas déconnecter l'utilisateur si c'est juste un bug temporaire
          }
        }
        if (attempt === maxRetries) throw lastError;
      }
    }
    throw lastError!;
  },
  isAuthenticated(): boolean { return !!localStorage.getItem('accessToken'); },
  getToken(): string | null { return localStorage.getItem('accessToken'); },
};

export const sharesAPI = {
  async shareToFeed(bookId: string, message: string = ''): Promise<{ success: boolean; message: string; share: any }> { return fetchAPI('/shares/feed', { method: 'POST', body: JSON.stringify({ bookId, message }) }); },
  async shareByEmail(bookId: string, emails: string[], message: string = ''): Promise<{ success: boolean; message: string }> { return fetchAPI('/shares/email', { method: 'POST', body: JSON.stringify({ bookId, emails, message }) }); },
  async getBookShares(bookId: string): Promise<{ shares: any[] }> { return fetchAPI(`/shares/book/${bookId}`); },
  async getFeedShares(page: number = 1, limit: number = 20): Promise<{ shares: any[]; pagination: any }> { const queryParams = new URLSearchParams({ page: page.toString(), limit: limit.toString() }); return fetchAPI(`/shares/feed?${queryParams}`); },
  async deleteShare(shareId: string): Promise<{ success: boolean; message: string }> { return fetchAPI(`/shares/${shareId}`, { method: 'DELETE' }); },
};

export const adminAPI = {
  async chatWithAdminAgent(messages: any[]): Promise<{ content: string; usedTools?: boolean }> { return fetchAPI('/api/admin/agent/chat', { method: 'POST', body: JSON.stringify({ messages }) }); },
  async getEmailTemplates() { return fetchAPI('/api/admin/agent/email-templates'); }
};
